import { useMemo, useState } from "react";
import { FolderPlus, Plus, Trash } from "@phosphor-icons/react";
import { CONTENT_LABELS, DYNAMIC_LABELS } from "./FolderLayoutPreview";

const CONTENT_TYPES = Object.keys(CONTENT_LABELS);
const DYNAMIC_TYPES = ["", ...Object.keys(DYNAMIC_LABELS)];

function makeKey(name = "folder") {
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || "folder";
  return `${slug}_${Math.random().toString(36).slice(2, 7)}`;
}

function newNode() {
  return {
    key: makeKey(), name_template: "New Folder", purpose: "",
    accepted_types: [], protection: "none", required: false,
    is_archive: false, is_drawings: false, dynamic_type: null,
    dynamic_naming: null, children: [],
  };
}

function updateNodeAtPath(nodes, path, updater) {
  const [index, ...rest] = path;
  return nodes.map((node, i) => {
    if (i !== index) return node;
    if (!rest.length) return updater(node);
    return { ...node, children: updateNodeAtPath(node.children || [], rest, updater) };
  });
}

function removeNodeAtPath(nodes, path) {
  const [index, ...rest] = path;
  if (!rest.length) return nodes.filter((_, i) => i !== index);
  return nodes.map((node, i) => i === index
    ? { ...node, children: removeNodeAtPath(node.children || [], rest) }
    : node);
}

function cleanNode(node) {
  const dynamicType = node.dynamic_type || null;
  return {
    ...node,
    key: node.key || makeKey(node.name_template),
    name_template: node.name_template?.trim() || null,
    purpose: node.purpose?.trim() || null,
    accepted_types: node.accepted_types?.length ? node.accepted_types : null,
    dynamic_type: dynamicType,
    dynamic_naming: dynamicType ? (node.dynamic_naming?.trim() || defaultDynamicNaming(dynamicType)) : null,
    children: node.children?.length ? node.children.map(cleanNode) : null,
  };
}

function defaultDynamicNaming(type) {
  if (type === "contractor_split") return "2.{num} {name}";
  if (type === "month_split") return "{year}-{month:02d} {month_name}";
  if (type === "subproject_split") return "{subproject_name}";
  if (type === "month_subproject_split") return "{year}-{month:02d} {month_name}";
  return "{name}";
}

function flattenEditorNodes(nodes) {
  const rows = [];
  const stack = [...nodes].reverse().map((node, index) => ({
    node,
    path: [nodes.length - index - 1],
    depth: 0,
  }));
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current.node || current.depth > 8) continue;
    rows.push(current);
    const children = Array.isArray(current.node.children) ? current.node.children : [];
    for (let index = children.length - 1; index >= 0; index -= 1) {
      stack.push({
        node: children[index],
        path: [...current.path, index],
        depth: current.depth + 1,
      });
    }
  }
  return rows;
}

function NodeEditorRow({ node, path, onUpdate, onRemove, depth = 0 }) {
  const patch = (updates) => onUpdate(path, (current) => ({ ...current, ...updates }));
  const toggleContent = (type) => patch({
    accepted_types: (node.accepted_types || []).includes(type)
      ? node.accepted_types.filter((item) => item !== type)
      : [...(node.accepted_types || []), type],
  });
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={`layout-node-${path.join("-")}`} style={{ marginLeft: Math.min(depth, 8) * 16 }}>
      <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-50/80">
        <span className="flex-1 truncate text-xs font-semibold text-slate-700">{node.name_template || node.dynamic_naming || "Untitled folder"}</span>
        <span className="text-[9px] text-slate-400">Level {depth + 1}</span>
        <button type="button" onClick={() => onRemove(path)} className="text-slate-400 hover:text-red-600" aria-label="Remove folder"><Trash size={13} /></button>
      </div>
      <div className="space-y-3 border-t border-slate-100 p-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Folder name
            <input value={node.name_template || ""} onChange={(e) => patch({ name_template: e.target.value })} placeholder="e.g. 2. Admin" className="w-full rounded-md border border-slate-200 px-2.5 py-2 text-sm font-normal normal-case tracking-normal text-slate-800" />
          </label>
          <label className="space-y-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Dynamic behavior
            <select value={node.dynamic_type || ""} onChange={(e) => patch({ dynamic_type: e.target.value || null, dynamic_naming: e.target.value ? defaultDynamicNaming(e.target.value) : null })} className="w-full rounded-md border border-slate-200 px-2.5 py-2 text-sm font-normal normal-case tracking-normal text-slate-800">
              <option value="">Fixed folder</option>{DYNAMIC_TYPES.filter(Boolean).map((type) => <option key={type} value={type}>{DYNAMIC_LABELS[type]}</option>)}
            </select>
          </label>
        </div>
        {node.dynamic_type && <label className="block space-y-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Generated folder naming pattern
          <input value={node.dynamic_naming || ""} onChange={(e) => patch({ dynamic_naming: e.target.value })} className="w-full rounded-md border border-violet-200 bg-violet-50/40 px-2.5 py-2 font-mono text-xs font-normal normal-case tracking-normal text-violet-800" />
        </label>}
        <label className="block space-y-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">What this folder is for
          <textarea value={node.purpose || ""} onChange={(e) => patch({ purpose: e.target.value })} rows={2} placeholder="Explain what belongs here so users and AI routing understand it." className="w-full resize-none rounded-md border border-slate-200 px-2.5 py-2 text-sm font-normal normal-case tracking-normal text-slate-800" />
        </label>
        <div>
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">What should go in it</p>
          <div className="flex flex-wrap gap-1.5">{CONTENT_TYPES.map((type) => <button key={type} type="button" onClick={() => toggleContent(type)} className={`rounded-full border px-2 py-1 text-[10px] font-medium ${(node.accepted_types || []).includes(type) ? "border-blue-200 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-500"}`}>{CONTENT_LABELS[type]}</button>)}</div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Protection
            <select value={node.protection || "none"} onChange={(e) => patch({ protection: e.target.value })} className="w-full rounded-md border border-slate-200 px-2.5 py-2 text-sm font-normal normal-case tracking-normal text-slate-800"><option value="none">Normal</option><option value="system">Structure locked</option><option value="protected">Fully protected</option></select>
          </label>
          <div className="flex flex-wrap items-end gap-3 pb-2 text-xs text-slate-600">
            {[["required","Required"],["is_drawings","Current drawings"],["is_archive","Drawing archive"]].map(([field, label]) => <label key={field} className="flex items-center gap-1.5"><input type="checkbox" checked={Boolean(node[field])} onChange={(e) => patch({ [field]: e.target.checked })} />{label}</label>)}
          </div>
        </div>
        <button type="button" onClick={() => patch({ children: [...(node.children || []), newNode()] })} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80"><FolderPlus size={13} />Add child folder</button>
      </div>
    </div>
  );
}

export default function FolderLayoutEditor({ initial, onSave, onCancel, saving }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [archiveStrategy, setArchiveStrategy] = useState(initial?.archive_strategy || "flat_archive");
  const [folders, setFolders] = useState(() => (initial?.folders || [newNode()]));
  const [autoFolders, setAutoFolders] = useState(() => (initial?.auto_folders || []));
  const [contractorsText, setContractorsText] = useState(() => (initial?.default_contractors || []).map((item) => item.name).join("\n"));
  const valid = useMemo(() => name.trim() && folders.length > 0 && folders.every((node) => node.name_template || node.dynamic_type), [name, folders]);
  const folderRows = useMemo(() => flattenEditorNodes(folders), [folders]);
  const autoFolderRows = useMemo(() => flattenEditorNodes(autoFolders), [autoFolders]);
  const updateFolders = (path, updater) => setFolders((current) => updateNodeAtPath(current, path, updater));
  const updateAutoFolders = (path, updater) => setAutoFolders((current) => updateNodeAtPath(current, path, updater));
  const submit = (e) => {
    e.preventDefault();
    if (!valid) return;
    const contractorNames = contractorsText.split("\n").map((item) => item.trim()).filter(Boolean);
    onSave({
      name: name.trim(), description: description.trim() || null,
      folders: folders.map(cleanNode), archive_strategy: archiveStrategy,
      default_contractors: contractorNames.length ? contractorNames.map((contractorName, index) => ({ num: index, name: contractorName, hint: null })) : null,
      auto_folders: autoFolders.length ? autoFolders.map(cleanNode) : null,
    });
  };
  return <form onSubmit={submit} className="space-y-5" data-testid="folder-layout-editor">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <label className="space-y-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Layout name
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Commercial Design Projects" data-testid="layout-name-input" className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-normal normal-case tracking-normal text-slate-800" />
      </label>
      <label className="space-y-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Drawing archive strategy
        <select value={archiveStrategy} onChange={(e) => setArchiveStrategy(e.target.value)} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-normal normal-case tracking-normal text-slate-800"><option value="flat_archive">One shared archive</option><option value="per_drawing_folder">Archive per drawing</option><option value="in_place">Keep revisions together</option></select>
      </label>
    </div>
    <label className="block space-y-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">When to use this layout
      <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Describe which project types this layout suits." className="w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-sm font-normal normal-case tracking-normal text-slate-800" />
    </label>
    <div><div className="mb-2 flex items-center justify-between"><div><h4 className="text-sm font-semibold text-slate-800">Project folders</h4><p className="text-[11px] text-slate-500">Define each folder, its purpose, and the content the AI should route there.</p></div><button type="button" onClick={() => setFolders((current) => [...current, newNode()])} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Plus size={12} />Top-level folder</button></div><div className="space-y-2">{folderRows.map((row) => <NodeEditorRow key={row.node.key || row.path.join("-")} node={row.node} path={row.path} depth={row.depth} onUpdate={updateFolders} onRemove={(path) => setFolders((current) => removeNodeAtPath(current, path))} />)}</div></div>
    <details className="rounded-lg border border-slate-200 bg-slate-50/50"><summary className="cursor-pointer px-4 py-3 text-xs font-semibold text-slate-700">Advanced: system-created folders and contractor defaults</summary><div className="space-y-4 border-t border-slate-200 p-4"><label className="block space-y-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Default contractor disciplines (one per line)<textarea value={contractorsText} onChange={(e) => setContractorsText(e.target.value)} rows={5} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-normal normal-case tracking-normal" /></label><div><div className="mb-2 flex items-center justify-between"><p className="text-xs font-semibold text-slate-700">Folders created later by automation</p><button type="button" onClick={() => setAutoFolders((current) => [...current, newNode()])} className="text-xs font-semibold text-primary">+ Add auto folder</button></div><div className="space-y-2">{autoFolderRows.map((row) => <NodeEditorRow key={row.node.key || row.path.join("-")} node={row.node} path={row.path} depth={row.depth} onUpdate={updateAutoFolders} onRemove={(path) => setAutoFolders((current) => removeNodeAtPath(current, path))} />)}</div></div></div></details>
    <div className="flex justify-end gap-2 border-t border-slate-100 pt-4"><button type="button" onClick={onCancel} disabled={saving} className="rounded-md bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700">Cancel</button><button type="submit" disabled={!valid || saving} data-testid="save-folder-layout-btn" className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">{saving ? "Saving…" : "Save layout"}</button></div>
  </form>;
}
